import React, { useState } from "react";
import { Box, Button, Grid, IconButton, Stack } from "@mui/material";
import { Helmet } from "react-helmet";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { DriveFileRenameOutline } from "@mui/icons-material";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import CustomLoader from "app/components/common/CustomLoader";
import { CustomTextField } from "assets/styles/globalStyledComponent";
import {
  getSessionIndependentSerialNumber,
  updateSessionIndependentSerialNumber,
} from "app/services/schoolService/schoolManagement.service";

const fieldConfig = [
  { key: "tcNumber", label: "TC Number" },
  { key: "employeeId", label: "Employee ID" },
  {
    key: "studentRegistrationCount",
    label: "Student Registration Count",
  },
  { key: "studentAdmissionCount", label: "Student Admission Count" },
  { key: "counselorNumberCount", label: "Counselor Number Count" },
];

const getInitialState = () =>
  fieldConfig.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {});

const SessionIndependent = () => {
  const [state, setState] = useState({
    ...getInitialState(),
    isChanged: false,
  });
  const [initialData, setInitialData] = useState(getInitialState());
  const [editingField, setEditingField] = useState(null);

  const {
    isLoading: listLoading,
    isFetching: listFetching,
    refetch,
  } = useQuery(
    ["getSessionIndependentSerialNumber"],
    () => getSessionIndependentSerialNumber(),
    {
      onSuccess: (response) => {
        const apiData =
          response?.data?.obj || response?.data?.data || response?.data || {};
        const normalizedData = fieldConfig.reduce((acc, field) => {
          const value = apiData?.[field.key];
          acc[field.key] =
            value === undefined || value === null ? "" : String(value);
          return acc;
        }, {});

        setState({ ...normalizedData, isChanged: false });
        setInitialData(normalizedData);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to load session independent serial numbers",
        );
      },
    },
  );

  const { mutate: updateSerialNumbers, isLoading: saveLoading } = useMutation(
    updateSessionIndependentSerialNumber,
    {
      onSuccess: (response) => {
        toast.success(
          response?.data?.message ||
            "Session independent serial numbers updated successfully",
        );
        refetch();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to update session independent serial numbers",
        );
      },
    },
  );

  const handleChange = (field, value) => {
    setState((prev) => {
      const updatedState = { ...prev, [field]: value };
      const hasChanged = fieldConfig.some(
        (item) => updatedState[item.key] !== initialData[item.key],
      );
      return { ...updatedState, isChanged: hasChanged };
    });
  };

  const validatePayload = () => {
    for (const field of fieldConfig) {
      const value = state[field.key];
      if (value === "") {
        toast.error(`${field.label} is required`);
        return false;
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        toast.error(`${field.label} must be a valid number`);
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!validatePayload()) {
      return;
    }

    const payload = fieldConfig.reduce((acc, field) => {
      acc[field.key] = Number(state[field.key]);
      return acc;
    }, {});

    updateSerialNumbers(payload);
  };

  const handleClear = () => {
    setState({ ...initialData, isChanged: false });
    setEditingField(null);
  };

  return (
    <>
      <Helmet>
        <title>Session Independent Serial Number</title>
      </Helmet>

      <CustomLoader show={listLoading || listFetching} />

      <Box sx={{ p: 3 }}>
        <Grid container spacing={2.5}>
          {fieldConfig.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={field.key}>
              <TextFieldLabel title={field.label} required />
              <Stack direction="row" alignItems="center" gap={1}>
                <CustomTextField
                  fullWidth
                  size="small"
                  type="number"
                  value={state[field.key]}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  inputProps={{ min: 0 }}
                  disabled={editingField !== field.key}
                />
                <IconButton
                  color="secondary"
                  onClick={() =>
                    setEditingField((prev) =>
                      prev === field.key ? null : field.key,
                    )
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      editingField === field.key ? "#0C2F49" : "#E3E9EF",
                    "&:hover": {
                      backgroundColor:
                        editingField === field.key ? "#0C2F49" : "#E3E9EF",
                    },
                    color: editingField === field.key ? "#FFFFFF" : "#0C2F49",
                  }}
                >
                  <DriveFileRenameOutline fontSize="medium" />
                </IconButton>
              </Stack>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Stack direction="row" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleClear}
                disabled={!state.isChanged || saveLoading}
              >
                Clear
              </Button>
              <LoadingButton
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleSave}
                disabled={!state.isChanged}
                loading={saveLoading}
              >
                Save
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default SessionIndependent;
